// src/RolTagsInput.js
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useInput, useDataProvider } from "react-admin";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

/**
 * Input pentru campul "rol" - text CSV in DB (ex: "responsabil,electrician"),
 * afisat/editat ca o lista de chip-uri. Apasa Enter sau virgula pentru a adauga
 * un rol nou; click pe X pe un chip pentru a-l elimina.
 *
 * Sugestiile de roluri NU sunt hardcodate - sunt extrase dinamic din rolurile
 * deja folosite de toti colaboratorii existenti (uniuni, fara duplicate).
 * Daca un rol nu mai e folosit de niciun colaborator, dispare automat din lista.
 */
export const RolTagsInput = ({ source = "rol", label = "Roluri / îndemânări" }) => {
    const { field } = useInput({ source });
    const dataProvider = useDataProvider();
    const [inputValue, setInputValue] = useState("");
    const [sugestiiExistente, setSugestiiExistente] = useState([]);

    const incarcaSugestii = useCallback(async () => {
        try {
            const { data } = await dataProvider.getList("colaboratori", {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: "id", order: "ASC" },
                filter: {},
            });
            const toateRolurile = data
                .flatMap((c) => (c.rol ? c.rol.split(",") : []))
                .map((r) => r.trim())
                .filter(Boolean);
            // unice, case-insensitive, pastrand prima forma de scriere intalnita
            const unice = [];
            const vazute = new Set();
            toateRolurile.forEach((r) => {
                const key = r.toLowerCase();
                if (!vazute.has(key)) {
                    vazute.add(key);
                    unice.push(r);
                }
            });
            setSugestiiExistente(unice.sort((a, b) => a.localeCompare(b)));
        } catch (err) {
            // esec silentios - sugestiile sunt un bonus, nu o functionalitate critica
            setSugestiiExistente([]);
        }
    }, [dataProvider]);

    useEffect(() => {
        incarcaSugestii();
    }, [incarcaSugestii]);

    const roluri = field.value
        ? field.value.split(",").map((r) => r.trim()).filter(Boolean)
        : [];

    const setRoluri = (listaNoua) => {
        field.onChange(listaNoua.join(","));
    };

    const adaugaRol = (rolBrut) => {
        const rol = rolBrut.trim();
        if (!rol) return;
        if (roluri.some((r) => r.toLowerCase() === rol.toLowerCase())) {
            setInputValue("");
            return; // deja exista, evitam duplicate
        }
        setRoluri([...roluri, rol]);
        setInputValue("");
        // adaugam optimist si in lista de sugestii, ca sa apara imediat pentru
        // urmatorii colaboratori editati in aceeasi sesiune, fara refetch
        setSugestiiExistente((prev) =>
            prev.some((s) => s.toLowerCase() === rol.toLowerCase()) ? prev : [...prev, rol].sort((a, b) => a.localeCompare(b))
        );
    };

    const stergeRol = (rol) => {
        setRoluri(roluri.filter((r) => r !== rol));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            adaugaRol(inputValue);
        } else if (e.key === "Backspace" && !inputValue && roluri.length) {
            stergeRol(roluri[roluri.length - 1]);
        }
    };

    const sugestiiDisponibile = sugestiiExistente.filter(
        (s) => !roluri.some((r) => r.toLowerCase() === s.toLowerCase())
    );

    return (
        <Box sx={{ width: "100%" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {label}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    p: 1,
                    border: "1px solid rgba(0,0,0,0.23)",
                    borderRadius: 1,
                    minHeight: 48,
                    alignItems: "center",
                }}
            >
                {roluri.map((rol) => (
                    <Chip key={rol} label={rol} size="small" onDelete={() => stergeRol(rol)} />
                ))}
                <TextField
                    variant="standard"
                    placeholder={roluri.length ? "" : "ex: responsabil, electrician"}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={() => adaugaRol(inputValue)}
                    InputProps={{ disableUnderline: true }}
                    sx={{ flexGrow: 1, minWidth: 120 }}
                />
            </Box>
            {!roluri.length && (
                <Typography variant="caption" color="text.secondary">
                    Niciun rol definit — apasă Enter sau virgulă pentru a adăuga unul
                </Typography>
            )}
            {sugestiiDisponibile.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                        Roluri folosite deja de alți colaboratori:
                    </Typography>
                    {sugestiiDisponibile.map((s) => (
                        <Chip
                            key={s}
                            label={`+ ${s}`}
                            size="small"
                            variant="outlined"
                            onClick={() => adaugaRol(s)}
                            sx={{ mr: 0.5, mb: 0.5, cursor: "pointer" }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
